/** @jsx jsx */
import { Global, jsx } from '@emotion/core';
import { FC, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import ReactGA from 'react-ga4';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Router, Switch } from 'react-router-dom';

import Footer from 'components/layouts/Footer';
import Header from 'components/layouts/Header';
import Loader from 'components/layouts/Loader';
import Notifier from 'components/layouts/Notifier';
import SsoErrorFlash from 'components/SsoErrorFlash';
import { globalStyles } from 'global-styles';
import {
  Auth,
  ChallengeDeclaration,
  Content,
  ContentCategory,
  DetailsPageDataLoader,
  HistoryOfDeclaredChallenges,
  NotFound,
  QuizPage,
} from 'pages';
import { useMediaQuery, useTheme } from '@material-ui/core';
import {
  appConfig,
  appOptions,
  checkAuth,
  featureByName,
  productCurrency,
} from 'actions/app.actions';
import { CookiesInfos } from 'components/CookiesInfos';
import { ICustomTheme, StoreOptionsType } from 'interfaces';
import {
  appOptionsSelector,
  appReducerSelector,
  authLoadingSelector,
  featureSelector,
  isAuthenticatedSelector,
  loginLoadingSelector,
  STATUS_NEED_TO_FILL_FIRST_LOGIN_FORM,
  userStatusSelector,
} from 'reducers/app.reducer';
import { AppAPI } from 'services/app.api';
import history from 'services/history';
import { ROUTES } from '../constants';
import {
  onAnalyticsConsentChange,
  setGoogleAnalyticsDisabled,
} from '../core/utils/google-analytics-consent';
import useExternalRedirect from '../core/utils/use-external-redirect.hook';
import { StyledContainer } from './styles';

// Set to '_cle' to be in sync with the v1 cookie management key
// This way both v1 and v2 use the same cookie to know if the cookie banner has been considered
export const ESSENTIAL_COOKIES = '_cle';

// Set by the blocking script in public/index.html.
const isOneTrustEnabled = !!window.MAX_ONE_TRUST_KEY;

type Props = {
  component: FC;
  [key: string]: unknown;
};

export const PrivateRoute = ({
  component: Component,
  ...rest
}: Props): JSX.Element => {
  const isAuthenticated: boolean = useSelector(isAuthenticatedSelector);
  const userStatus = useSelector(userStatusSelector);

  const location = rest.location as { pathname: string };

  if (!isAuthenticated) {
    if (location.pathname === ROUTES.HOME)
      return <Redirect to={ROUTES.LOGIN} />;

    const encodedReturnPath = encodeURIComponent(
      window.location.href.slice(window.location.origin.length),
    );

    return <Redirect to={`${ROUTES.LOGIN}?returnPath=${encodedReturnPath}`} />;
  }

  // User has a token but hasn't completed the first-login form yet
  if (userStatus === STATUS_NEED_TO_FILL_FIRST_LOGIN_FORM) {
    return <Redirect to={ROUTES.SUBSCRIBE} />;
  }

  return <Route {...rest} component={Component} />;
};

const URL_AUTH_TOKEN = 'authToken';
function useTokenHandler() {
  const dispatch = useDispatch();

  // Nettoie l'éventuel authToken legacy encore présent dans l'URL (rétro-compatibilité).
  // Le cookie max_api_token est désormais posé par Symfony via /v2/auth/first-login/activate.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has(URL_AUTH_TOKEN)) {
      urlParams.delete(URL_AUTH_TOKEN);
      window.history.pushState(
        null,
        '',
        `${window.location.pathname}${
          urlParams.toString() ? `?${urlParams.toString()}` : ''
        }`,
      );
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Toujours tenter GET /me au montage — Lexik utilise le cookie max_api_token HttpOnly
  useEffect(() => {
    dispatch(checkAuth.request());
  }, [dispatch]);
}

export const App = (): JSX.Element => {
  useTokenHandler();

  const dispatch = useDispatch();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { loading: appLoading } = useSelector(appReducerSelector);
  const authLoading = useSelector(authLoadingSelector);
  const loginLoading = useSelector(loginLoadingSelector);
  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const storeFeature = useSelector(featureSelector('use_store'));
  const options: StoreOptionsType = useSelector(appOptionsSelector);
  const { isRedirecting } = useExternalRedirect();
  const title = options.list?.find(
    (option) => option.name === 'UI_META_TITLE',
  )?.value;
  const [cookie, setCookie] = useCookies([ESSENTIAL_COOKIES]);
  const isEssentialCookieConsidered = !!cookie[ESSENTIAL_COOKIES];
  const isLoginPage = window.location.pathname.includes(ROUTES.LOGIN);

  const theme: ICustomTheme = useTheme();
  const isDesktop = !useMediaQuery(theme.breakpoints.down('sm'));

  const [isHeaderReady, setIsHeaderReady] = useState(false);
  const [showHeaderLoader, setShowHeaderLoader] = useState(true);

  // Mirrors boutique/CMS2: hide the whole page behind a loader until the
  // shared header reports it's mounted ("customHeaderReady"), so header,
  // content and footer all reveal together once the real header height is
  // known (no layout shift, no header popping in after the rest).
  // The header isn't shown on the login page, so don't make it wait there.
  useEffect(() => {
    if (isLoginPage) {
      setIsHeaderReady(true);
      return undefined;
    }

    const handleReady = () => setIsHeaderReady(true);
    window.addEventListener('customHeaderReady', handleReady);
    const timeout = window.setTimeout(handleReady, 5000);

    return () => {
      window.removeEventListener('customHeaderReady', handleReady);
      window.clearTimeout(timeout);
    };
  }, [isLoginPage]);

  // Keep the overlay mounted long enough for its fade-out transition to play.
  useEffect(() => {
    if (!isHeaderReady) {
      return undefined;
    }
    const timeout = window.setTimeout(() => setShowHeaderLoader(false), 300);
    return () => window.clearTimeout(timeout);
  }, [isHeaderReady]);

  // The shared header (mounted by Header) fetches its own 'use_store' state
  // directly from MAX's API, but Footer still reads it from Redux, so it
  // still needs to be requested here.
  useEffect(() => {
    if (isAuthenticated && !storeFeature?.isLoading) {
      dispatch(featureByName.request('use_store'));
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch(appConfig.request());
    dispatch(appOptions.request());
    dispatch(productCurrency.request());

    AppAPI.getGAConfig().then((response) => {
      const measurementId = response.data.ua;
      if (!measurementId) {
        return;
      }
      // Without OneTrust there is no consent signal: GA loads right away.
      if (!isOneTrustEnabled) {
        ReactGA.initialize(measurementId);
        return;
      }
      let isInitialized = false;
      onAnalyticsConsentChange((granted) => {
        setGoogleAnalyticsDisabled(measurementId, !granted);
        if (granted && !isInitialized) {
          ReactGA.initialize(measurementId);
          isInitialized = true;
        }
      });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isAppLoading =
    isRedirecting ||
    appLoading ||
    authLoading ||
    options.isLoading ||
    loginLoading;
  // Same loader element from startup until the header is ready: the spinner never restarts.
  const isLoaderVisible = isAppLoading || !isHeaderReady;
  const isLoaderMounted = isAppLoading || showHeaderLoader;

  const cookiesBanner = isOneTrustEnabled ? null : (
    <CookiesInfos
      isEssentialCookieConsidered={isEssentialCookieConsidered}
      setCookie={(key, value, options) =>
        setCookie(key as typeof ESSENTIAL_COOKIES, value, options)
      }
    />
  );

  return (
    <Router history={history}>
      <div>
        <Global styles={globalStyles} />
        {isLoaderMounted && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 9999,
              backgroundColor: '#fff',
              transition: 'opacity 0.3s ease-out',
              opacity: isLoaderVisible ? 1 : 0,
              pointerEvents: isLoaderVisible ? 'auto' : 'none',
            }}
          >
            <Loader label={t('auth.login.logging_in')} />
          </div>
        )}
        {!isAppLoading && (
          <StyledContainer
            isDesktop={isDesktop}
            isLoginPage={isLoginPage}
            style={
              isHeaderReady
                ? undefined
                : { visibility: 'hidden', height: '100vh', overflow: 'hidden' }
            }
          >
            <Helmet>
              <html lang={language || 'fr'} />
              {title && <title>{title}</title>}
            </Helmet>
            <Notifier />
            <SsoErrorFlash />
            <Header />
            <Switch>
              <Route path={ROUTES.LOGIN}>
                <Auth />
              </Route>
              <PrivateRoute path={ROUTES.HOME} exact component={Content} />
              <PrivateRoute
                path={`${ROUTES.CONTENT}/:id`}
                component={Content}
              />
              <PrivateRoute
                path={`${ROUTES.CATEGORY_CONTENTS}/:id`}
                component={ContentCategory}
              />
              <PrivateRoute
                path={`${ROUTES.CHALLENGE}/:id${ROUTES.DECLARE}`}
                component={ChallengeDeclaration}
              />
              <PrivateRoute
                path={`${ROUTES.CHALLENGE}${ROUTES.HISTORY}/:id?`}
                component={HistoryOfDeclaredChallenges}
              />
              <PrivateRoute
                path={`${ROUTES.CHALLENGE}/:id`}
                component={DetailsPageDataLoader}
              />
              <PrivateRoute path={`${ROUTES.FORM}/:id`} component={QuizPage} />
              <PrivateRoute component={NotFound} />
            </Switch>
            <Footer />
          </StyledContainer>
        )}
        {cookiesBanner}
      </div>
    </Router>
  );
};

export default App;
